/**
 * Shared expert-review UI: QuestionCard.tsx.
 *
 * Belongs here: reusable presentational piece + question analytics (view + committed answer).
 * Does not belong here: flow rules or API.
 *
 * Analytics: see question contract in expertReviewEvents.ts — fire once on commit, not on every tap.
 */
import { useEffect, useRef } from 'react'
import { EventNames } from '../../../../../types/misc.types'
import { trackExpertReviewQuestionEvent } from '../../analytics/expertReviewEvents'
import type {
  Question,
  QuestionOptionReturnMetadata,
  StrategyQuestionnaireAnswersMap
} from '../types/questionnaire.types'
import { getResolvedQuestion } from '../../question-utils/visibleQuestions'
import { QuestionTitle } from './QuestionTitle'
import { RadioOptionRow } from './RadioOptionRow'
import { AmountInput } from './AmountInput'
import { ExpertRecommendation } from './ExpertRecommendation'

export interface QuestionCardOption {
  id: string
  displayText: string
  subText?: string
  expertRecommended?: boolean
  order?: number
  metadata?: QuestionOptionReturnMetadata | null
}

interface QuestionCardProps {
  question: Question
  answers: StrategyQuestionnaireAnswersMap
  onAdvance: (questionId: string, value: string | number) => void
  /**
   * Selection highlight only — writes draftAnswers, does not move the cursor.
   */
  updateDraftAnswer: (questionId: string, value: string | number) => void
  onAmountClear?: () => void
  amountHelperText?: string
  /** stageId or preferenceTypeId — analytics scrollSection + test locators */
  contextId?: string
  /**
   * ExpertReviewScreen.* when tracking. Optional — shared by rebalance questionnaire;
   * skip question analytics when omitted.
   */
  analyticsScreen?: string
}

/** One question’s UI (title, inputs, recommendation). No slide or navigation. */
export function QuestionCard({
  question: rawQuestion,
  answers,
  onAdvance,
  updateDraftAnswer,
  onAmountClear,
  amountHelperText,
  contextId,
  analyticsScreen
}: QuestionCardProps) {
  // Filter options by conditionalExpression before rendering
  const question = getResolvedQuestion(rawQuestion, answers)
  const questionId = question.id
  const questionLocator = contextId ? `${contextId}-${questionId}` : questionId
  const optionLocator = (optionId: string) =>
    contextId ? `${contextId}-${questionId}-${optionId}` : `${questionId}-${optionId}`
  const inputType = question.input?.type ?? 'SINGLE_SELECT'
  const cardOptions: QuestionCardOption[] = (question.options ?? []).map((option) => ({
    id: option.id,
    displayText: option.displayText,
    subText: option.subText,
    expertRecommended: option.expertRecommended,
    order: option.order,
    metadata: option.metadata
  }))
  const selectedValue = answers[questionId]
  const effectiveSelected =
    selectedValue == null
      ? undefined
      : Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue

  // After first auto-select / confirm, further option taps are ignored until question changes
  const hasChosenOptionRef = useRef(false)
  useEffect(() => {
    hasChosenOptionRef.current = false
  }, [questionId])

  // Question visible — identity only (no-op when analyticsScreen omitted, e.g. rebalance)
  useEffect(() => {
    if (!analyticsScreen) return
    trackExpertReviewQuestionEvent({
      eventName: EventNames.ScreenLoaded,
      ScreenName: analyticsScreen,
      questionId,
      scrollSection: contextId
    })
  }, [analyticsScreen, questionId, contextId])

  /** Commit a single-option answer (responseName = optionId). */
  function trackOptionCommitted(optionId: string, eventName: typeof EventNames.UserClicked | typeof EventNames.ButtonTap) {
    if (!analyticsScreen) return
    trackExpertReviewQuestionEvent({
      eventName,
      ScreenName: analyticsScreen,
      questionId,
      scrollSection: contextId,
      responseName: optionId
    })
  }

  /** @returns false when tap should be ignored (row skips animation / confirm side effects). */
  function trySelectOption(option: QuestionCardOption): boolean {
    if (hasChosenOptionRef.current) return false
    if (inputType === 'SINGLE_SELECT') {
      // Auto-advance path — select is the commit
      hasChosenOptionRef.current = true
      trackOptionCommitted(option.id, EventNames.UserClicked)
    }
    // SINGLE_SELECT_CONFIRM — highlight only; commit on Confirm
    updateDraftAnswer(questionId, option.id)
    return true
  }

  return (
    <div>
      <div className={`${inputType === 'AMOUNT' ? 'mb-4' : 'mb-6'}`}>
        <QuestionTitle
          displayText={question.displayText}
          subText={question.subText}
          testLocator={questionLocator}
        />
      </div>

      {inputType === 'AMOUNT' && (
        <AmountInput
          questionId={questionId}
          label={question.input?.title ?? question.input?.placeholder ?? 'Amount'}
          value={typeof selectedValue === 'number' ? selectedValue : undefined}
          onClear={onAmountClear}
          minValue={question.input?.validations?.minValue ?? 0}
          maxValue={question.input?.validations?.maxValue ?? 999999999}
          onConfirm={(amountValue) => {
            if (analyticsScreen) {
              trackExpertReviewQuestionEvent({
                eventName: EventNames.ButtonTap,
                ScreenName: analyticsScreen,
                questionId,
                scrollSection: contextId,
                amount: amountValue
              })
            }
            onAdvance(questionId, amountValue)
          }}
          helperText={amountHelperText}
          confirmButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
          testLocator={questionLocator}
        />
      )}

      {(inputType === 'SINGLE_SELECT' || inputType === 'SINGLE_SELECT_CONFIRM') && (
        <div className="rounded-[16px] border border-gray-200 overflow-hidden bg-white">
          {cardOptions.map((option, index) => (
            <RadioOptionRow
              key={option.id}
              option={{
                id: option.id,
                displayText: option.displayText,
                subText: option.subText,
                expertRecommended: option.expertRecommended,
                metadata: option.metadata
              }}
              selected={effectiveSelected === option.id}
              onSelect={() => trySelectOption(option)}
              showSeparator={index < cardOptions.length - 1}
              requiresConfirm={inputType === 'SINGLE_SELECT_CONFIRM'}
              onConfirm={
                inputType === 'SINGLE_SELECT_CONFIRM' && effectiveSelected != null
                  ? () => {
                    if (hasChosenOptionRef.current) return
                    hasChosenOptionRef.current = true
                    trackOptionCommitted(String(effectiveSelected), EventNames.ButtonTap)
                    onAdvance(questionId, effectiveSelected as string)
                  }
                  : undefined
              }
              onSelectionAnimationEnd={
                inputType === 'SINGLE_SELECT'
                  ? () => onAdvance(questionId, option.id)
                  : undefined
              }
              testLocator={option.id ? optionLocator(option.id) : undefined}
            />
          ))}
        </div>
      )}

      {question.expertRecommendation && (
        <ExpertRecommendation
          displayText={question.expertRecommendation.displayText}
          subText={question.expertRecommendation.subText}
        />
      )}
    </div>
  )
}
